import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth, errorMessage } from '../../context/AuthContext';
import { TimerField, FeedbackField, CsvQuestionsPicker } from '../../components/qcm/QcmShared';
import { LETTERS } from '../../utils/qcmCsv';

let uidCounter = 0;
const uid = () => `k${++uidCounter}`;
const blankQuestion = () => ({
  key: uid(), text: '', explanation: '',
  options: [{ key: uid(), text: '', correct: false }, { key: uid(), text: '', correct: false }]
});
const withKeys = (questions) => questions.map(q => ({
  key: uid(), text: q.text || '', explanation: q.explanation || '',
  options: (q.options || []).map(o => ({ key: uid(), text: o.text || '', correct: !!o.correct }))
}));

// Mêmes règles que le serveur, pour signaler les problèmes avant l'envoi
function validate(form, questions) {
  const errs = [];
  if (!form.title.trim()) errs.push('Le titre est obligatoire');
  if (!form.subject_id) errs.push('Choisissez un sujet');
  if (form.timer_enabled && !(form.timer_minutes >= 1 && form.timer_minutes <= 600)) errs.push('Durée du timer : entre 1 et 600 minutes');
  if (form.published && questions.length === 0) errs.push('Un QCM publié doit contenir au moins une question');
  questions.forEach((q, i) => {
    const filled = q.options.filter(o => o.text.trim());
    if (!q.text.trim()) errs.push(`Question ${i + 1} : l'énoncé est vide`);
    if (filled.length < 2) errs.push(`Question ${i + 1} : il faut au moins 2 choix remplis`);
    if (!filled.some(o => o.correct)) errs.push(`Question ${i + 1} : cochez au moins une bonne réponse`);
  });
  return errs;
}

export default function AdminQcmEdit() {
  const { id } = useParams();
  const isNew = !id;
  const [params] = useSearchParams();
  const { authFetch } = useAuth();
  const navigate = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [form, setForm] = useState({
    title: '', description: '', subject_id: Number(params.get('sujet')) || '',
    timer_enabled: false, timer_minutes: 10, feedback_mode: 'end', published: true
  });
  const [questions, setQuestions] = useState(() => (isNew ? [blankQuestion()] : []));
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(isNew);
  const [flash, setFlash] = useState(() => {
    const n = params.get('importe');
    if (n) return { type: 'ok', text: `✓ QCM créé avec ${n} question(s) importée(s).` };
    if (params.get('cree')) return { type: 'ok', text: '✓ QCM créé' };
    return null;
  });
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvQuestions, setCsvQuestions] = useState([]);
  const bottomRef = useRef(null);

  useEffect(() => {
    authFetch('/api/admin/qcm/subjects').then(r => r.ok ? r.json() : []).then(setSubjects).catch(() => {});
    if (!isNew) {
      authFetch(`/api/admin/qcm/quizzes/${id}`)
        .then(async r => { if (!r.ok) throw new Error(await errorMessage(r)); return r.json(); })
        .then(d => {
          setForm({
            title: d.title, description: d.description || '', subject_id: d.subject_id,
            timer_enabled: d.timer_enabled, timer_minutes: d.timer_minutes || 10, feedback_mode: d.feedback_mode || 'end', published: d.published
          });
          setQuestions(withKeys(d.questions));
          setLoaded(true);
        })
        .catch(e => setFlash({ type: 'error', text: `Chargement impossible : ${e.message}` }));
    }
    // eslint-disable-next-line
  }, [id]);

  // Prévenir avant de fermer l'onglet s'il reste des modifications
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  const setMeta = (patch) => { setForm(prev => ({ ...prev, ...patch })); setDirty(true); };
  const editQuestions = (fn) => { setQuestions(prev => fn(prev)); setDirty(true); };

  const updateQuestion = (qk, patch) => editQuestions(qs => qs.map(q => (q.key === qk ? { ...q, ...patch } : q)));
  const updateOption = (qk, ok, patch) => editQuestions(qs => qs.map(q => (q.key !== qk ? q : {
    ...q, options: q.options.map(o => (o.key === ok ? { ...o, ...patch } : o))
  })));
  const addOption = (qk) => editQuestions(qs => qs.map(q => (q.key !== qk || q.options.length >= LETTERS.length ? q : {
    ...q, options: [...q.options, { key: uid(), text: '', correct: false }]
  })));
  const removeOption = (qk, ok) => editQuestions(qs => qs.map(q => (q.key !== qk || q.options.length <= 2 ? q : {
    ...q, options: q.options.filter(o => o.key !== ok)
  })));
  const moveQuestion = (idx, dir) => editQuestions(qs => {
    const t = idx + dir;
    if (t < 0 || t >= qs.length) return qs;
    const c = [...qs];
    [c[idx], c[t]] = [c[t], c[idx]];
    return c;
  });
  const removeQuestion = (qk, idx) => {
    const q = questions.find(x => x.key === qk);
    if (q && (q.text.trim() || q.options.some(o => o.text.trim())) && !window.confirm(`Supprimer la question ${idx + 1} ?`)) return;
    editQuestions(qs => qs.filter(x => x.key !== qk));
  };
  const addQuestion = () => {
    editQuestions(qs => [...qs, blankQuestion()]);
    setTimeout(() => bottomRef.current && bottomRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };
  const appendCsv = () => {
    editQuestions(qs => [...qs.filter(q => q.text.trim() || q.options.some(o => o.text.trim())), ...withKeys(csvQuestions)]);
    setFlash({ type: 'ok', text: `✓ ${csvQuestions.length} question(s) ajoutée(s) — pensez à enregistrer` });
    setShowImport(false); setCsvQuestions([]);
  };

  const save = async (e) => {
    e.preventDefault();
    const errs = validate(form, questions);
    setErrors(errs);
    if (errs.length) { window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setSaving(true); setFlash(null);
    const payload = {
      ...form,
      subject_id: Number(form.subject_id),
      questions: questions.map(q => ({
        text: q.text, explanation: q.explanation,
        options: q.options.filter(o => o.text.trim()).map(o => ({ text: o.text, correct: o.correct }))
      }))
    };
    try {
      const r = await authFetch(isNew ? '/api/admin/qcm/quizzes' : `/api/admin/qcm/quizzes/${id}`, {
        method: isNew ? 'POST' : 'PUT', body: JSON.stringify(payload)
      });
      if (!r.ok) throw new Error(await errorMessage(r));
      setDirty(false);
      if (isNew) {
        const d = await r.json();
        navigate(`/admin/qcm/${d.id}?cree=1`, { replace: true });
        return;
      }
      setFlash({ type: 'ok', text: '✓ QCM enregistré' });
      setTimeout(() => setFlash(f => (f && f.text === '✓ QCM enregistré' ? null : f)), 2500);
    } catch (err) {
      setFlash({ type: 'error', text: `Non enregistré : ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const leave = () => {
    if (dirty && !window.confirm('Des modifications ne sont pas enregistrées. Quitter quand même ?')) return;
    navigate(form.subject_id ? `/admin/qcm/sujets/${form.subject_id}` : '/admin/qcm');
  };

  if (!loaded) return <div>{flash ? <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div> : 'chargement…'}</div>;

  const subjectName = (subjects.find(s => s.id === Number(form.subject_id)) || {}).name;

  return (
    <div>
      <button type="button" className="qcm-back" onClick={leave}>← {subjectName || 'retour'}</button>
      <h1 style={{ fontStyle: 'italic', marginBottom: '1.5rem' }}>{isNew ? 'Nouveau QCM' : 'Modifier le QCM'}</h1>

      {flash && <div className={`flash ${flash.type === 'error' ? 'error' : ''}`}>{flash.text}</div>}
      {!form.published && (
        <div className="qcm-draft-banner">
          <span><strong>Brouillon :</strong> ce QCM n'est pas visible sur le site.</span>
          <button type="button" className="btn" onClick={() => setMeta({ published: true })}>le publier</button>
        </div>
      )}
      {form.published && dirty && !isNew && <p className="qcm-help" style={{ marginBottom: '1rem' }}>Pensez à enregistrer pour que les modifications apparaissent sur le site.</p>}
      {errors.length > 0 && (
        <div className="flash error">
          <strong>À corriger avant d'enregistrer :</strong>
          <ul>{errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
        </div>
      )}

      <form onSubmit={save}>
        {/* ---------- Informations ---------- */}
        <div className="qcm-panel">
          <h2 className="qcm-panel-title">1. Informations</h2>
          <div className="qcm-grid-2">
            <div className="form-group">
              <label>Titre</label>
              <input value={form.title} onChange={e => setMeta({ title: e.target.value })} required />
            </div>
            <div className="form-group">
              <label>Sujet</label>
              <select value={form.subject_id} onChange={e => setMeta({ subject_id: Number(e.target.value) })} required>
                <option value="">— choisir —</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description (facultative)</label>
            <input value={form.description} onChange={e => setMeta({ description: e.target.value })} />
          </div>
          <TimerField enabled={form.timer_enabled} minutes={form.timer_minutes} onChange={setMeta} />
          <FeedbackField value={form.feedback_mode} onChange={setMeta} />
          <div className="form-group">
            <label>
              <input type="checkbox" checked={form.published} onChange={e => setMeta({ published: e.target.checked })} /> Publié (visible sur le site)
            </label>
          </div>
        </div>

        {/* ---------- Questions ---------- */}
        <div className="qcm-questions-head">
          <h2 className="qcm-panel-title" style={{ margin: 0 }}>2. Questions ({questions.length})</h2>
          <button type="button" className="btn secondary" onClick={() => setShowImport(s => !s)}>
            {showImport ? 'fermer l\'import' : '⇪ ajouter depuis un CSV'}
          </button>
        </div>

        {showImport && (
          <div className="qcm-panel">
            <CsvQuestionsPicker onReady={(qs) => setCsvQuestions(qs)} />
            <button type="button" className="btn" disabled={csvQuestions.length === 0} onClick={appendCsv}>
              ajouter {csvQuestions.length || ''} question(s) à la fin
            </button>
          </div>
        )}

        {questions.length === 0 && <div className="empty-state">Aucune question. Ajoutez-en une ou importez un CSV.</div>}

        {questions.map((q, idx) => {
          const multiple = q.options.filter(o => o.correct).length > 1;
          return (
            <div key={q.key} className="qcm-q-editor" ref={idx === questions.length - 1 ? bottomRef : null}>
              <div className="qcm-q-editor-head">
                <strong>Question {idx + 1}</strong>
                <div className="admin-row-actions">
                  <button type="button" className="btn secondary" onClick={() => moveQuestion(idx, -1)} disabled={idx === 0} title="monter">↑</button>
                  <button type="button" className="btn secondary" onClick={() => moveQuestion(idx, 1)} disabled={idx === questions.length - 1} title="descendre">↓</button>
                  <button type="button" className="btn danger" onClick={() => removeQuestion(q.key, idx)} title="supprimer la question">✕</button>
                </div>
              </div>
              <div className="form-group">
                <label>Énoncé</label>
                <textarea className="qcm-textarea" value={q.text} onChange={e => updateQuestion(q.key, { text: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Choix — cochez la ou les bonnes réponses {multiple && <span className="qcm-multi-tag">plusieurs réponses</span>}</label>
                {q.options.map((o, j) => (
                  <div key={o.key} className={`qcm-opt-row ${o.correct ? 'correct' : ''}`}>
                    <span className="qcm-opt-letter">{LETTERS[j]}</span>
                    <input value={o.text} onChange={e => updateOption(q.key, o.key, { text: e.target.value })} placeholder={`choix ${LETTERS[j]}`} />
                    <label className="qcm-opt-correct" title="bonne réponse">
                      <input type="checkbox" checked={o.correct} onChange={e => updateOption(q.key, o.key, { correct: e.target.checked })} />
                      <span>bonne</span>
                    </label>
                    <button type="button" className="qcm-opt-remove" onClick={() => removeOption(q.key, o.key)} disabled={q.options.length <= 2} title="retirer ce choix">✕</button>
                  </div>
                ))}
                {q.options.length < LETTERS.length && (
                  <button type="button" className="qcm-link-btn" onClick={() => addOption(q.key)}>+ ajouter un choix</button>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Explication (facultative, montrée après la correction)</label>
                <textarea className="qcm-textarea small" value={q.explanation} onChange={e => updateQuestion(q.key, { explanation: e.target.value })} />
              </div>
            </div>
          );
        })}

        <button type="button" className="btn secondary" onClick={addQuestion} style={{ marginBottom: '5rem' }}>+ ajouter une question</button>

        {/* ---------- Barre d'enregistrement ---------- */}
        <div className="qcm-savebar">
          <span>{dirty ? '● modifications non enregistrées' : '✓ à jour'}</span>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button type="button" className="btn secondary" onClick={leave}>retour</button>
            <button type="submit" className="btn" disabled={saving}>{saving ? 'enregistrement…' : (isNew ? 'créer le QCM' : 'enregistrer')}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
